import { ArrowUpRight, Download, ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/Motion";

const BOARD_IMG = "/patents-certification-board.png";

const ASSETS = [
  {
    region: "Taiwan",
    title: "ISO 22716:2007 — Cosmetics GMP",
    body: "Certificate of scope for the manufacture of liquid and powder cosmetic products. Certificate No. TW007795, version 1, issued 12-01-2020. Manufacture/production cycle 15-01-2019 through 14-01-2025.",
  },
  {
    region: "Japan",
    title: "Patent certificate — JP6188249",
    body: "Granted patent certificate No. JP6188249 (日本專利證書), covering the group's exosome platform technology in the Japanese market.",
  },
  {
    region: "Switzerland",
    title: "Patent certificate — CH710255",
    body: "Swiss patent certificate (瑞士專利證書 CH710255) — for skin care (用於皮膚護理). Issued as Urkunde · Certificat · Certificato 710255.",
  },
  {
    region: "Switzerland",
    title: "Patent certificate — CH710157",
    body: "Swiss patent certificate (瑞士專利證書 CH710157) — promote hair improvement.",
  },
  {
    region: "South Korea",
    title: "MFDS cosmetic registration",
    body: "Manufactured with a Korean medical-equipment partner into a cosmetic skin booster, registered in the cosmetics category with Korea's Ministry of Food and Drug Safety (MFDS) and marketed domestically.",
  },
  {
    region: "Malaysia",
    title: "NPRA cosmetic registration",
    body: "Cosmetic registration approval from Malaysia's National Pharmaceutical Regulatory Agency (NPRA), opening Southeast-Asian skin, hair and chronic-wound health-care markets.",
  },
  {
    region: "Canada",
    title: "Cosmetic Notification Form",
    body: "Cosmetic Notification Form filed with Health Canada. A CNF is a regulatory notification required before sale in Canada — it is not a Health Canada product approval.",
  },
  {
    region: "United States",
    title: "FDA listing",
    body: "U.S. Food & Drug Administration, and the first company in its category to secure dual MOHW (Taiwan) approvals.",
  },
];

export default function Certification() {
  return (
    <div className="pt-16" data-testid="certification-page">
      {/* Hero */}
      <section className="border-b border-[#e4e9ec]">
        <div className="mx-auto max-w-[1440px] px-5 pb-14 pt-20 md:px-10 md:pb-16 md:pt-24">
          <Reveal>
            <p className="font-lab text-[11px] uppercase tracking-[0.24em] text-[#0e8c8c]">
              Exoplus Bio Group — Regulatory Assets
            </p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl">
              Patents &amp; Certification
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#56636b]">
              Strategic foresight in regulatory compliance and product documentation — patents,
              GMP certification and market registrations across eight jurisdictions, presented as
              a single regulatory board.
            </p>
          </Reveal>
        </div>
      </section>

      {/* The regulatory board (full document) */}
      <section className="py-14 md:py-20">
        <div className="mx-auto max-w-[1200px] px-5 md:px-10">
          <Reveal>
            <div className="border border-[#e4e9ec] bg-white p-3 shadow-sm sm:p-5">
              <a
                href={BOARD_IMG}
                target="_blank"
                rel="noreferrer"
                data-testid="certification-board-link"
                className="block"
                aria-label="Open the patents and certification board at full size"
              >
                <img
                  src={BOARD_IMG}
                  alt="Exoplus Bio Group patents and certification board — ISO 22716 GMP certificate, Japanese and Swiss patents, MFDS, NPRA, Health Canada and FDA regulatory assets"
                  className="mx-auto w-full object-contain"
                  loading="eager"
                />
              </a>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-[#e4e9ec] pt-4">
                <p className="font-lab text-[11px] uppercase tracking-[0.18em] text-[#56636b]">
                  Official board — 1672 × 618 source document
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <a
                    href={BOARD_IMG}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="certification-open-full"
                    className="inline-flex items-center gap-2 border border-[#0a0a0a] px-5 py-2.5 text-sm transition-colors hover:bg-[#0a0a0a] hover:text-white"
                  >
                    Open full size <ArrowUpRight className="h-4 w-4" />
                  </a>
                  <a
                    href={BOARD_IMG}
                    download="exoplusbio-patents-certification-board.png"
                    data-testid="certification-download"
                    className="inline-flex items-center gap-2 bg-[#0a0a0a] px-5 py-2.5 text-sm text-white transition-colors hover:bg-[#0e8c8c]"
                  >
                    <Download className="h-4 w-4" /> Download board
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Structured index of the assets on the board */}
      <section className="border-t border-[#e4e9ec] py-14 md:py-20">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          <Reveal>
            <p className="font-lab text-[11px] uppercase tracking-[0.24em] text-[#0e8c8c]">
              Index
            </p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl">
              Every asset on the board, at a glance
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {ASSETS.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.05}>
                <div className="flex h-full flex-col border border-[#e4e9ec] bg-white p-6" data-testid="certification-asset-card">
                  <p className="font-lab text-[10px] uppercase tracking-[0.2em] text-[#0e8c8c]">{a.region}</p>
                  <h3 className="mt-3 font-display text-lg leading-snug">{a.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-[#56636b]">{a.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Verification note */}
      <section className="border-t border-[#e4e9ec] py-12">
        <div className="mx-auto flex max-w-[900px] flex-col items-center gap-4 px-5 text-center md:px-10">
          <ShieldCheck className="h-6 w-6 text-[#0e8c8c]" />
          <p className="text-sm leading-relaxed text-[#56636b]" data-testid="certification-disclaimer">
            Certification and regulatory images are presented as supporting materials for
            discussion and verification. Manufacturer certificates document scope of
            certification and patent grants only — they are not Health Canada or FDA product
            authorizations, and no therapeutic claims are made or implied.
          </p>
          <p className="font-lab text-[10px] uppercase tracking-[0.2em] text-[#8a959c]">
            Professional review before publication of any claim
          </p>
        </div>
      </section>
    </div>
  );
}
